import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { trackCtaClick } from '../utils/analytics'

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [productsOpen, setProductsOpen] = useState(false)
  const [solutionsOpen, setSolutionsOpen] = useState(false)
  const [pricingOpen, setPricingOpen] = useState(false)
  const location = useLocation()

  const isActive = (path: string) => location.pathname === path
  const isProductsActive = () => location.pathname.startsWith('/products')
  const isSolutionsActive = () => location.pathname.startsWith('/solutions')
  const isPricingActive = () => location.pathname.startsWith('/pricing')

  return (
    <header className="bg-white border-b border-navy-100 sticky top-0 z-50">
      <nav className="container-custom">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src="/logo.svg" alt="Klarhead" className="h-8 lg:h-10" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:space-x-8">
            {/* Products Dropdown */}
            <div className="relative">
              <button
                onClick={() => { setProductsOpen(!productsOpen); setSolutionsOpen(false) }}
                onBlur={() => setTimeout(() => setProductsOpen(false), 150)}
                className={`flex items-center text-sm font-medium transition-colors ${
                  isProductsActive() ? 'text-teal-600' : 'text-navy-600 hover:text-navy-900'
                }`}
              >
                Products
                <svg className="ml-1 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {productsOpen && (
                <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-navy-100 py-2">
                  <Link
                    to="/products/psa"
                    className="flex items-start px-4 py-3 hover:bg-navy-50"
                  >
                    <div className="w-10 h-10 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-navy-900">Klarhead PSA</div>
                      <div className="text-xs text-navy-500">Professional Services Automation</div>
                    </div>
                  </Link>
                  <Link
                    to="/products/wms"
                    className="flex items-start px-4 py-3 hover:bg-navy-50"
                  >
                    <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-navy-900">Klarhead WMS</span>
                      </div>
                      <div className="text-xs text-navy-500">Warehouse Management System</div>
                    </div>
                  </Link>
                  <div className="border-t border-navy-100 mt-2 pt-2">
                    <Link
                      to="/products"
                      className="block px-4 py-2 text-sm text-navy-600 hover:bg-navy-50 hover:text-navy-900"
                    >
                      Compare products →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Solutions Dropdown */}
            <div className="relative">
              <button
                onClick={() => { setSolutionsOpen(!solutionsOpen); setProductsOpen(false) }}
                onBlur={() => setTimeout(() => setSolutionsOpen(false), 150)}
                className={`flex items-center text-sm font-medium transition-colors ${
                  isSolutionsActive() ? 'text-teal-600' : 'text-navy-600 hover:text-navy-900'
                }`}
              >
                Solutions
                <svg className="ml-1 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {solutionsOpen && (
                <div className="absolute left-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-navy-100 py-2">
                  {/* Warehouses Section */}
                  <div className="px-4 py-2">
                    <div className="text-xs font-semibold text-navy-400 uppercase tracking-wider">For Warehouses</div>
                  </div>
                  <Link
                    to="/solutions/3pl"
                    className="block px-4 py-2 text-sm text-navy-600 hover:bg-navy-50 hover:text-navy-900"
                  >
                    3PL & Fulfillment
                  </Link>
                  <Link
                    to="/solutions/distribution"
                    className="block px-4 py-2 text-sm text-navy-600 hover:bg-navy-50 hover:text-navy-900"
                  >
                    Distribution & Wholesale
                  </Link>

                  <div className="border-t border-navy-100 my-2"></div>

                  {/* Professional Services Section */}
                  <div className="px-4 py-2">
                    <div className="text-xs font-semibold text-navy-400 uppercase tracking-wider">For Professional Services</div>
                  </div>
                  <Link
                    to="/solutions/law"
                    className="block px-4 py-2 text-sm text-navy-600 hover:bg-navy-50 hover:text-navy-900"
                  >
                    Law Firms
                  </Link>
                  <Link
                    to="/solutions/consulting"
                    className="block px-4 py-2 text-sm text-navy-600 hover:bg-navy-50 hover:text-navy-900"
                  >
                    Consulting
                  </Link>
                  <Link
                    to="/solutions/engineering"
                    className="block px-4 py-2 text-sm text-navy-600 hover:bg-navy-50 hover:text-navy-900"
                  >
                    Engineering
                  </Link>
                </div>
              )}
            </div>

            {/* Pricing Dropdown */}
            <div className="relative">
              <button
                onClick={() => { setPricingOpen(!pricingOpen); setProductsOpen(false); setSolutionsOpen(false) }}
                onBlur={() => setTimeout(() => setPricingOpen(false), 150)}
                className={`flex items-center text-sm font-medium transition-colors ${
                  isPricingActive() ? 'text-teal-600' : 'text-navy-600 hover:text-navy-900'
                }`}
              >
                Pricing
                <svg className="ml-1 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {pricingOpen && (
                <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-navy-100 py-2">
                  <Link
                    to="/pricing/psa"
                    className="flex items-start px-4 py-3 hover:bg-navy-50"
                  >
                    <div className="w-10 h-10 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-navy-900">Klarhead PSA</div>
                      <div className="text-xs text-navy-500">Professional Services</div>
                    </div>
                  </Link>
                  <Link
                    to="/pricing/wms"
                    className="flex items-start px-4 py-3 hover:bg-navy-50"
                  >
                    <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-navy-900">Klarhead WMS</span>
                      </div>
                      <div className="text-xs text-navy-500">Warehouse Management</div>
                    </div>
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/services/audit"
              className={`text-sm font-medium transition-colors ${
                isActive('/services/audit') ? 'text-teal-600' : 'text-navy-600 hover:text-navy-900'
              }`}
            >
              Audit
            </Link>

            <Link
              to="/trust"
              className={`text-sm font-medium transition-colors ${
                isActive('/trust') ? 'text-teal-600' : 'text-navy-600 hover:text-navy-900'
              }`}
            >
              Trust
            </Link>
          </div>

          {/* CTA Button */}
          <div className="hidden lg:flex lg:items-center">
            <Link to="/contact" className="btn-primary" onClick={() => trackCtaClick('header_demo')}>
              Book a Demo
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-md text-navy-600 hover:bg-navy-50"
            aria-label="Toggle navigation menu"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-navy-100">
            <div className="flex flex-col space-y-4">
              {/* Products */}
              <div>
                <div className="text-xs font-semibold text-navy-400 uppercase tracking-wider mb-2">Products</div>
                <Link
                  to="/products/psa"
                  className="block text-base font-medium text-navy-600 hover:text-navy-900 py-1"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Klarhead PSA
                </Link>
                <Link
                  to="/products/wms"
                  className="block text-base font-medium text-navy-600 hover:text-navy-900 py-1"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Klarhead WMS
                </Link>
              </div>

              {/* Solutions */}
              <div>
                <div className="text-xs font-semibold text-navy-400 uppercase tracking-wider mb-2">Solutions</div>
                <Link
                  to="/solutions"
                  className="block text-base font-medium text-navy-600 hover:text-navy-900 py-1"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  All Solutions
                </Link>
              </div>

              {/* Pricing */}
              <div>
                <div className="text-xs font-semibold text-navy-400 uppercase tracking-wider mb-2">Pricing</div>
                <Link
                  to="/pricing/psa"
                  className="block text-base font-medium text-navy-600 hover:text-navy-900 py-1"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Klarhead PSA
                </Link>
                <Link
                  to="/pricing/wms"
                  className="block text-base font-medium text-navy-600 hover:text-navy-900 py-1"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Klarhead WMS
                </Link>
              </div>
              <Link
                to="/services/audit"
                className="text-base font-medium text-navy-600 hover:text-navy-900"
                onClick={() => setMobileMenuOpen(false)}
              >
                Audit
              </Link>
              <Link
                to="/trust"
                className="text-base font-medium text-navy-600 hover:text-navy-900"
                onClick={() => setMobileMenuOpen(false)}
              >
                Trust
              </Link>
              <Link
                to="/contact"
                className="btn-primary text-center"
                onClick={() => { trackCtaClick('header_demo_mobile'); setMobileMenuOpen(false) }}
              >
                Book a Demo
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  )
}
